import { createBrowserRouter } from 'react-router';
import type { RouteObject } from 'react-router';
import { RootLayout } from '../shared/ui/RootLayout';
import { AdminShell } from '../modules/admin/AdminShell';
import { AdminGuard } from '../modules/admin/AdminGuard';
import { AdminHome } from '../modules/admin/AdminHome';
import { NotFound } from '../shared/ui/NotFound';
import { LandingResolver } from '../modules/venue/LandingResolver';
import { VenueRoute } from '../modules/venue/VenueContext';
import { VenueShell } from '../modules/venue/VenueShell';
import { VenueHome } from '../modules/venue/VenueHome';
import { LoginPage } from '../modules/auth/LoginPage';
import { ChangePasswordPage } from '../modules/auth/ChangePasswordPage';
import { RequireAuth } from '../modules/auth/RequireAuth';
import { SystemStatusGate } from '../modules/system/SystemStatusGate';
import { SetupWizard } from '../modules/system/SetupWizard';
import { UsersPage } from '../modules/users/UsersPage';
import { RolesPage } from '../modules/roles/RolesPage';
import { BrandPage } from '../modules/franchise/BrandPage';
import { CountryPoliciesPage } from '../modules/franchise/CountryPoliciesPage';
import { VenuesPage } from '../modules/venues/VenuesPage';
import { CreateVenuePage } from '../modules/venues/CreateVenuePage';
import { RoomsPage } from '../modules/venues/RoomsDialog';
import { BookingSettingsPage } from '../modules/venues/BookingSettingsPage';
import { OperatingHoursPage } from '../modules/venues/OperatingHoursPage';
import { AuditPage } from '../modules/audit/AuditPage';
import { EmployeesPage } from '../modules/workforce/EmployeesPage';
import { ShiftsPage } from '../modules/workforce/ShiftsPage';
import { ProductsPage } from '../modules/products/ProductsPage';
import { CreatePassPage } from '../modules/products/CreatePassPage';
import { EditPassPage } from '../modules/products/EditPassPage';
import { CustomersPage } from '../modules/multivisitpasses/CustomersPage';
import { MvpCardNumbersPage } from '../modules/multivisitpasses/MvpCardNumbersPage';
import { MvpDefinitionPage } from '../modules/multivisitpasses/MvpDefinitionPage';
import { AdminProductsPage } from '../modules/products/AdminProductsPage';
import { DiscountsPage } from '../modules/discounts/DiscountsPage';
import { CreateDiscountCodePage } from '../modules/discounts/CreateDiscountCodePage';
import { EditDiscountCodePage } from '../modules/discounts/EditDiscountCodePage';
import { InventoryPage } from '../modules/products/InventoryPage';
import { BookingListPage } from '../modules/bookings/BookingListPage';
import { PosPaymentPage } from '../modules/sales/PosPaymentPage';
import { PosDevicesPage } from '../modules/pos-devices/PosDevicesPage';
import { PosStationsPage } from '../modules/pos-stations/PosStationsPage';
import { PublicWaiverPage } from '../modules/public-waivers/PublicWaiverPage';
import { AdminWaiverTemplateFormPage, AdminWaiversPage } from '../modules/venues/AdminWaiversPage';
import { CapacityOverviewPage } from '../modules/venue/CapacityOverviewPage';
import { VenueWaiverPage } from '../modules/venue/VenueWaiverPage';

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: RootLayout,
    children: [
      {
        Component: SystemStatusGate,
        children: [
          { path: 'setup', Component: SetupWizard },
          { path: 'login', Component: LoginPage },
          { path: 'public/waivers/:venueId/sign', Component: PublicWaiverPage },
          {
            Component: RequireAuth,
            children: [
              { index: true, Component: LandingResolver },
              {
                // The Venue workspace is its own area with its own shell, distinct from the
                // franchise AdminShell (ADR 0015) — a sibling, never nested under it.
                path: 'loc/:venueId',
                Component: VenueRoute,
                children: [
                  {
                    Component: VenueShell,
                    children: [
                      { index: true, Component: VenueHome },
                      { path: 'capacity', Component: CapacityOverviewPage },
                      { path: 'customers', Component: CustomersPage },
                      { path: 'mvp-card-numbers', Component: MvpCardNumbersPage },
                      { path: 'shifts', Component: ShiftsPage },
                      { path: 'products', Component: ProductsPage },
                      { path: 'products/new/:type', Component: CreatePassPage },
                      { path: 'products/:passId/edit', Component: EditPassPage },
                      { path: 'products/multi-visit-passes/new', Component: MvpDefinitionPage },
                      {
                        path: 'products/multi-visit-passes/:definitionId',
                        Component: MvpDefinitionPage,
                      },
                      { path: 'inventory', Component: InventoryPage },
                      { path: 'bookings', Component: BookingListPage },
                      { path: 'pos/sale/:saleId', Component: PosPaymentPage },
                      { path: 'pos-devices', Component: PosDevicesPage },
                      { path: 'pos-stations', Component: PosStationsPage },
                      { path: 'rooms', Component: RoomsPage },
                      { path: 'booking-settings', Component: BookingSettingsPage },
                      { path: 'operating-hours', Component: OperatingHoursPage },
                      { path: 'waiver', Component: VenueWaiverPage },
                      { path: '*', Component: NotFound },
                    ],
                  },
                ],
              },
              // Change-password is reachable from either area (the account menu lives in both
              // shells) and pins forced-change users before they have a workspace, so it sits
              // outside the admin guard and shell.
              { path: 'change-password', Component: ChangePasswordPage },
              {
                // The Franchise admin area is its own scope under `/admin/...` (ADR 0015): its
                // own shell, no Venue switcher. The guard redirects a user without any
                // franchise-wide admin permission back to `/` with a toast (UX only).
                path: 'admin',
                Component: AdminGuard,
                children: [
                  {
                    Component: AdminShell,
                    children: [
                      { index: true, Component: AdminHome },
                      { path: 'users', Component: UsersPage },
                      { path: 'roles', Component: RolesPage },
                      { path: 'brand', Component: BrandPage },
                      { path: 'country-policies', Component: CountryPoliciesPage },
                      { path: 'products', Component: AdminProductsPage },
                      { path: 'discounts', Component: DiscountsPage },
                      { path: 'discounts/new', Component: CreateDiscountCodePage },
                      { path: 'discounts/:id/edit', Component: EditDiscountCodePage },
                      { path: 'venues', Component: VenuesPage },
                      { path: 'venues/new', Component: CreateVenuePage },
                      { path: 'waivers', Component: AdminWaiversPage },
                      { path: 'waivers/new', Component: AdminWaiverTemplateFormPage },
                      {
                        path: 'waivers/:templateId/versions/new',
                        Component: AdminWaiverTemplateFormPage,
                      },
                      { path: 'employees', Component: EmployeesPage },
                      { path: 'audit', Component: AuditPage },
                      { path: '*', Component: NotFound },
                    ],
                  },
                ],
              },
              { path: '*', Component: NotFound },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
